/**
 * Utilities for clearing application data and cache
 */

/**
 * Clears all localStorage and sessionStorage data
 */
export const clearStorage = () => {
  try {
    localStorage.clear();
    sessionStorage.clear();
    console.log('Storage cleared successfully');
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

/**
 * Clears browser cache using Cache API
 */
export const clearBrowserCache = async () => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Browser cache cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing browser cache:', error);
  }
};

/**
 * Clears IndexedDB data
 */
export const clearIndexedDB = async () => {
  try {
    if ('indexedDB' in window) {
      // Get all databases
      const databases = await indexedDB.databases();
      
      // Delete each database
      await Promise.all(
        databases.map(database => {
          return new Promise((resolve, reject) => {
            const deleteReq = indexedDB.deleteDatabase(database.name);
            deleteReq.onsuccess = () => resolve();
            deleteReq.onerror = () => reject(deleteReq.error);
          });
        })
      );
      console.log('IndexedDB cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing IndexedDB:', error);
  }
};

/**
 * Clears service worker cache and unregisters service workers
 */
export const clearServiceWorker = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => registration.unregister())
      );
      console.log('Service workers cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing service workers:', error);
  }
};

/**
 * Clears all application data including storage, cache, IndexedDB and service workers
 * @param {boolean} reloadPage - Whether to reload the page after clearing data
 */
/**
 * Quick clear function that clears storage and reloads immediately
 * This is more reliable for the UI state issue
 */
export const quickClearAndReload = () => {
  console.log('Quick clearing all data and reloading...');
  
  // Clear storage immediately
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {
    console.error('Error clearing storage:', e);
  }
  
  // Clear cache in background and reload immediately
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    }).catch(e => console.error('Error clearing cache:', e));
  }
  
  // Unregister service workers in background
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    }).catch(e => console.error('Error unregistering SW:', e));
  }
  
  // Reload immediately
  setTimeout(() => {
    window.location.replace(window.location.href);
  }, 100);
};

export const clearAllAppData = async (reloadPage = true) => {
  try {
    console.log('Starting to clear all application data...');
    
    // Clear browser cache first
    await clearBrowserCache();
    
    // Clear IndexedDB 
    await clearIndexedDB();
    
    // Clear service workers
    await clearServiceWorker();
    
    // Clear storage last (after async operations) to ensure everything is cleaned
    clearStorage();
    
    console.log('All application data cleared successfully');
    
    // Force reload page immediately after clearing
    if (reloadPage) {
      // Use location.replace to prevent back button issues
      window.location.replace(window.location.href);
    }
    
  } catch (error) {
    console.error('Error clearing application data:', error);
    // Even if there's an error, try to reload if requested
    if (reloadPage) {
      setTimeout(() => {
        window.location.replace(window.location.href);
      }, 500);
    }
    throw error;
  }
};
